import React, { FC, useCallback, useMemo } from 'react';
import PriceSection from './PriceSection';
import { useSelector } from 'react-redux';
import currentDealSelectors from '../../../../store/selectors/currentDealSelectors';
import { IFinanceQuote } from '../../../../../common/model/IFinanceQuote';
import { IPrice } from '../../../../../../../common/model/IPrice';
import { CustomiseSections } from '../../../../../common/enum/CustomiseSections';
import partExchangeSelectors from '../../../../store/selectors/partExchangeSelectors';
import useScrollToSection from '../../../../react/navigation/useScrollToSection';
import usePriceLoadingState from '../../../../react/usePriceLoadingState';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';

interface IPriceSectionContainerProps {
  setIsFinCalcSpcModalOpen: (value: boolean) => void;
  isFinCalcSpcModalOpen: boolean;
}

const PriceSectionContainer: FC<IPriceSectionContainerProps> = ({
  setIsFinCalcSpcModalOpen,
  isFinCalcSpcModalOpen,
}) => {
  const dealError = useSelector(currentDealSelectors.getErrorStatus);
  const { scrollToSection } = useScrollToSection();
  const financeQuote: IFinanceQuote = useSelector(currentDealSelectors.getFinanceQuote);
  const secondaryCurrencyQuote: IFinanceQuote = useSelector(currentDealSelectors.getSecondaryCurrencyQuote);
  const primaryPriceTotalPayable: IPrice = useSelector(currentDealSelectors.getTotalAmountPayable);
  const isDataLoading = usePriceLoadingState();
  const hasTradeIn = Boolean(useSelector(partExchangeSelectors.getPartExchange));
  const isMultiCurrency = useSelector(currentDealSelectors.getIsMultiCurrency);
  const hasComparisonRate = useSelector(currentDealSelectors.getFinanceQuoteComparisonRate);
  const shouldDisplayFinance = !!financeQuote?.isPersonalized;
  const {
    platformConfig: { priceDisplay },
  } = usePlatformConfig();

  const onFinanceCalculate = useCallback(() => {
    setIsFinCalcSpcModalOpen(true);
  }, [isFinCalcSpcModalOpen]);

  const onDriveawayIndicativeClick = useCallback(() => {
    scrollToSection(CustomiseSections.DISCLAIMER);
  }, []);

  const isCash = useMemo(
    () => !isFinCalcSpcModalOpen && financeQuote?.isCashProduct,
    [isFinCalcSpcModalOpen, financeQuote],
  );

  return (
    <PriceSection
      loading={isDataLoading}
      disabled={!!dealError || isDataLoading}
      financeQuote={financeQuote}
      secondaryCurrencyQuote={secondaryCurrencyQuote}
      isCash={isCash}
      hasTradeIn={hasTradeIn}
      isMultiCurrency={isMultiCurrency}
      hasComparisonRate={hasComparisonRate}
      primaryPrice={primaryPriceTotalPayable}
      shouldDisplayFinance={shouldDisplayFinance}
      onFinanceCalculate={onFinanceCalculate}
      onDriveawayIndicativeClick={onDriveawayIndicativeClick}
      priceDisplay={priceDisplay}
    />
  );
};

export default PriceSectionContainer;
